import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import _ from 'lodash';
import { Store } from '@ngrx/store';
import * as PublishersActions from '../_store/publishers.actions';
import * as fromPublishers from '../_store/publishers.reducer';

import { FormHelper } from '../../../../_common';
import { JapiQuery, ResponseFromServer, ThinPublisher, PublisherEdit, AppFilter, AppTable } from '../../../../shared/_models/models';
import { ApiService } from '../../../../shared/_services/api.service';
import { SharedService } from '../../../../shared/_services/shared.service';
import { SignupType } from '../../../signup/_services/signup.config';
import { PhoneValidationService } from '../../../../shared/_services/phone-validation.service';
import { formGroupParamsByPaymentMethod } from './publishers.config';
import { PUBLISHER_FORECAST_PERMISSION_NAME } from 'src/app/shared/_constants/publishers';
import { COUNTRY_PHONE_CODES } from '../../../../shared/_components/phone-input/config/phone-input.config';
import { Region, Country } from '../../../../shared/_models/models';
import { DealPartner } from '../../../../shared/_models/models';
import { PublishersApiService, CreatePublisherPayload, PublisherApiResponse } from './publishers.api-service';

@Injectable({ providedIn: 'root' })
export class SupplyPublisherService {

  constructor(
    private http: HttpClient,
    private fh: FormHelper,
    private store: Store<fromPublishers.PublishersState>,
    private api: ApiService,
    private sharedService: SharedService,
    private phoneValidationService: PhoneValidationService,
    private publishersApiService: PublishersApiService
  ) {}

  getFinancialDocumentFile(publisherId, fileId): any {
    return this.http.get(`${environment.javaApiUrl}/publisher/${publisherId}/financialDocument/${fileId}`, { responseType: 'blob' });
  }

  createPublisher(payload: CreatePublisherPayload): Observable<PublisherApiResponse> {
    return this.publishersApiService.createPublisher(payload);
  }

  registerUserJava(payload: any, signupType: SignupType): Observable<any> {
    return this.api.registerPublisher(payload, signupType);
  }

  getPublishers(query: any) {
    this.store.dispatch(new PublishersActions.FetchPublishersRequestAction());
    this.api.getPublishers(query)
      .subscribe((res: any) => {
        this.store.dispatch(new PublishersActions.FetchPublishersSuccessAction({ publishers: res }));
      },
      (err: any) => {
        this.store.dispatch(new PublishersActions.FetchPublishersFailureAction(err));
        this.sharedService.onHttpCompletionResolve(false, 'Failure', 'Publishers fetch failed',
          '', '');
      });
  }
  getFilteredPublishers(query: JapiQuery): Observable<ResponseFromServer<ThinPublisher[]>> {
    return this.api.getPublishers(query);
  }
  deletePublisher(publisherId: number, publisherFilters: AppFilter[], publishersTable: AppTable): any {
    const queryPublishers = this.sharedService.buildTableQuery(publisherFilters, publishersTable,
      ['publisherInfo', 'publisherTrafficSources']);

    this.publishersApiService.deletePublisher(publisherId)
      .subscribe((res: any) => {
        // this.store.dispatch(new PublishersActions.UpdatePublisher({updatedPublisher: res}));
        this.sharedService.onHttpCompletionResolve(true, 'Success', 'Publisher was deleted',
          '', '');
        this.getPublishers(queryPublishers);
      },
      (err: any) => {
        this.sharedService.onHttpCompletionResolve(false, 'Failure', 'Publisher delete failed',
          '', '');
      });
  }

  getPublisherById(publisherId: number): Observable<PublisherEdit> {
    return this.api.getPublisherById(publisherId);
  }

  updatePublisherById(publisher: PublisherEdit): Observable<PublisherEdit> {
    return this.api.updatePublisherById(publisher);
  }

  // Transform functions

  // get from api
  transformPublisherPermissionsFromApi(currentPublisher) {
    const permissions = {};
    currentPublisher.permissions.forEach(permission => {
      switch (permission.name) {
        case 'publisher.reporting.deals':
          permissions['reportingDealPermission'] = true;
          break;
        case 'publisher.reporting.detailed':
          permissions['reportingDetailedPermission'] = true;
          break;
        case 'publisher.reporting.demand':
          permissions['reportingDemandPermission'] = true;
          break;
        case 'publisher.deal.show':
          permissions['dealsShowPermission'] = true;
          break;
        case 'publisher.floors.show':
          permissions ['floorsShowPermission'] = true;
          break;
        // case 'publisher.spearad.show':
        //   permissions ['spearadShowPermission'] = true;
        //   break;
        case PUBLISHER_FORECAST_PERMISSION_NAME:
          permissions ['dealForecastPermission'] = true;
          break;
        default:
          break;
      }
    });
    return permissions;
  }

  transformPublisherBlockListsFromApi(currentPublisher: PublisherEdit): void {
    currentPublisher.btype = currentPublisher.btype?.map(x => Number(x));
    currentPublisher.battr = currentPublisher.battr?.map(x => Number(x));
    currentPublisher.dspAccountExcludeList = currentPublisher.dspAccountExcludeList?.map(x => Number(x));
    currentPublisher.dealPartnerExcludeList = currentPublisher.dealPartnerExcludeList?.map(x => x === 'all' ? x : Number(x));
  }

  // send to api
  transformPublisherPermissionsToApi(currentPublisher): void {
    const publisherPermissions = [{ name: 'publisher.reporting.default' }];
    if (currentPublisher.reportingDealPermission) {
      publisherPermissions.push({ name: 'publisher.reporting.deals' });
    }
    if (currentPublisher.reportingDetailedPermission) {
      publisherPermissions.push({ name: 'publisher.reporting.detailed' });
    }
    if (currentPublisher.reportingDemandPermission) {
      publisherPermissions.push({ name: 'publisher.reporting.demand' });
    }
    if (currentPublisher.dealsShowPermission) {
      publisherPermissions.push({ name: 'publisher.deal.show' });
      if (currentPublisher.dealForecastPermission) {
        publisherPermissions.push({ name: PUBLISHER_FORECAST_PERMISSION_NAME });
      }
    }
    if (currentPublisher.floorsShowPermission) {
      publisherPermissions.push({ name: 'publisher.floors.show' });
    }

    // if (currentPublisher.spearadShowPermission) {
    //   publisherPermissions.push({name: 'publisher.spearad.show'});
    // }

    delete currentPublisher.floorsShowPermission;
    delete currentPublisher.reportingDealPermission;
    delete currentPublisher.reportingDemandPermission;
    delete currentPublisher.reportingDetailedPermission;
    delete currentPublisher.dealsShowPermission;
    delete currentPublisher.dealForecastPermission;
    currentPublisher.permissions = publisherPermissions;
  }

  transformPublisherInfoToApi(currentPublisherInfo): any {
    if (currentPublisherInfo.phoneInfo) {
      if (currentPublisherInfo.phoneInfo.phone && currentPublisherInfo.phoneInfo.phoneCountryCode) {
        currentPublisherInfo.phone = this.phoneValidationService.formatPhoneWithCountryCode(
          currentPublisherInfo.phoneInfo.phoneCountryCode,
          currentPublisherInfo.phoneInfo.phone
        );
      }
      // Remove the phoneInfo property as it's not expected by the API
      const { phoneInfo, ...publisherInfoWithoutPhoneInfo } = currentPublisherInfo;
      currentPublisherInfo = publisherInfoWithoutPhoneInfo;
    }
    // Handle the older format with separate fields for backward compatibility
    else if (currentPublisherInfo.countryCode && currentPublisherInfo.phone) {
      const country = COUNTRY_PHONE_CODES.find(c => c.countryCode === currentPublisherInfo.countryCode);
      const isoToUse = country ? country.iso : 'US';
      currentPublisherInfo.phone = this.phoneValidationService.formatPhoneWithCountryCode(
        isoToUse,
        currentPublisherInfo.phone
      );
    }

    const { phoneCountryCode, ...publisherInfoWithoutPhoneCode } = currentPublisherInfo;
    currentPublisherInfo = publisherInfoWithoutPhoneCode;
    
    currentPublisherInfo.adServerTech = currentPublisherInfo.adServerTechId ? { id: currentPublisherInfo.adServerTechId } : null;
    currentPublisherInfo.bizdevOwner = currentPublisherInfo.bizdevOwnerId ? { id: currentPublisherInfo.bizdevOwnerId } : null;
    currentPublisherInfo.operationsOwner = currentPublisherInfo.operationsOwnerId ? { id: currentPublisherInfo.operationsOwnerId } : null;
    currentPublisherInfo.samOwner = currentPublisherInfo.samOwnerId ? { id: currentPublisherInfo.samOwnerId } : null;
    delete currentPublisherInfo.adServerTechId;
    delete currentPublisherInfo.bizdevOwnerId;
    delete currentPublisherInfo.operationsOwnerId;
    delete currentPublisherInfo.samOwnerId;
    currentPublisherInfo.propertyNotes = (currentPublisherInfo.propertyNotes) ? currentPublisherInfo.propertyNotes.join(',') : '';
    return currentPublisherInfo;
  }

  transformPublisherBlockedListsToApi(currentPublisher): void {
    currentPublisher.bcat = (currentPublisher.bcat && currentPublisher.bcat.length !== 0) ? currentPublisher.bcat : null;
    currentPublisher.btype = (currentPublisher.btype && currentPublisher.btype.length !== 0) ? currentPublisher.btype : null;
    currentPublisher.battr = (currentPublisher.battr && currentPublisher.battr.length !== 0) ? currentPublisher.battr : null;
    currentPublisher.badv = (currentPublisher.badv && currentPublisher.badv.length !== 0) ? currentPublisher.badv : null;
    currentPublisher.tldBadv = (currentPublisher.tldBadv && currentPublisher.tldBadv.length !== 0) ? currentPublisher.tldBadv : null;
    currentPublisher.dspAccountExcludeList = (currentPublisher.dspAccountExcludeList && currentPublisher.dspAccountExcludeList.length !== 0) ? currentPublisher.dspAccountExcludeList : null;
    currentPublisher.dealPartnerExcludeList = (currentPublisher.dealPartnerExcludeList && currentPublisher.dealPartnerExcludeList.length !== 0) ? currentPublisher.dealPartnerExcludeList : null;
  }

  transformPublisherBlockedCountriesToApi(
    blockedCountries: { [region: string]: number[] },
    regions: Array<{ region: string; regionId: number }>
  ): { [regionId: number]: number[] } {
    const regionKeyToId = regions.reduce((acc, r) => {
      acc[r.region] = r.regionId;
      return acc;
    }, {} as { [region: string]: number });

    const result: { [regionId: number]: number[] } = {};
    Object.entries(blockedCountries).forEach(
      ([regionKey, countryIds]) => {
        const regionId = regionKeyToId[regionKey];
        if (regionId && Array.isArray(countryIds)) {
          result[regionId] = countryIds;
        }
      }
    );
    return result;
  }

  transformPublisherBlockedCountriesFromApi(
    publisher: any,
    regions: Array<{ region: string; regionId: number }>
  ): { [region: string]: number[] } {
    if (!publisher.blockedCountries) return {};
    const result: { [region: string]: number[] } = {};
    Object.entries(publisher.blockedCountries).forEach(
      ([regionIdStr, countryIds]) => {
        const region = regions.find(r => r.regionId === +regionIdStr);
        if (region && Array.isArray(countryIds)) {
          result[region.region] = countryIds as number[];
        }
      }
    );
    return result;
  }

  // Payment functions
  getFormGroupParamsByPaymentMethod(type) {
    if (!type) {
      return {};
    }
    return _.cloneDeep(formGroupParamsByPaymentMethod[type]);
  }

  getPaymentEntity(publisher, type, id) {
    if (id === null || type === null || type === 'NONE') {
      return {};
    }
    const paymentEntity = _.cloneDeep(publisher.payment[`publisherPaymentMethod${_.capitalize(type)}`]);
    return paymentEntity || {};
  }

  getAllowedRegions(filter: { allowedRegions?: string[] }): Observable<ResponseFromServer<Region[]>> {
    return this.api.getAllowedRegions(filter);
  }

  getCountries(filter: any): Observable<ResponseFromServer<Country[]>> {
    return this.api.getCountries(filter);
  }

  getActiveDealPartners(): Observable<ResponseFromServer<DealPartner[]>> {
    const query = {
      filter: {
        filters: [
          {
            fieldName: 'status',
            operation: 'IN',
            value: ['ACTIVE']
          }
        ]
      }
    };
    return this.api.getDealPartners(query);
  }
}
